import {
  type MutableRefObject,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
} from "react";

import { ArrowsInSimple, ArrowsOutSimple, Minus, Plus } from "@phosphor-icons/react";

import { mapFrameClasses } from "./mapFrameClasses";
import { MapLoadingPill } from "./MapLoadingPill";
import { defaultIconProps } from "../../features/shared/ui/icons";

type MapShellProps = {
  mapContainerRef?:
    | RefObject<HTMLDivElement | null>
    | MutableRefObject<HTMLDivElement | null>;
  mapContent?: ReactNode;
  mapClassName?: string;
  wrapperClassName?: string;
  fabContent?: ReactNode;
  placeholder?: ReactNode;
  status?: { isMapReady: boolean; hasRenderedTiles: boolean };
  loadingLabel?: string | null;
  isExpanded: boolean;
  onToggle: () => void;
  toggleLabels: {
    expandButton: string;
    collapseButton: string;
    expandAria: string;
    collapseAria: string;
  };
  disableToggle?: boolean;
  fabAriaLabel?: string;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  zoomAria?: { in: string; out: string };
};

export function MapShell({
  mapContainerRef,
  mapClassName,
  wrapperClassName,
  fabContent,
  mapContent,
  placeholder,
  status,
  loadingLabel,
  isExpanded,
  onToggle,
  toggleLabels,
  disableToggle = false,
  fabAriaLabel,
  onZoomIn,
  onZoomOut,
  zoomAria,
}: MapShellProps) {
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (status?.hasRenderedTiles) {
      hasLoadedRef.current = true;
    }
  }, [status?.hasRenderedTiles]);

  return (
    <div
      className={[
        mapFrameClasses.wrapper,
        isExpanded ? mapFrameClasses.wrapperExpanded : "",
        wrapperClassName ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={mapFrameClasses.controls}>
        {onZoomOut ? (
          <button
            type="button"
            className={mapFrameClasses.controlButton}
            onClick={onZoomOut}
            aria-label={zoomAria?.out ?? "Zoom out"}
          >
            <Minus {...defaultIconProps} aria-hidden />
          </button>
        ) : null}
        {onZoomIn ? (
          <button
            type="button"
            className={mapFrameClasses.controlButton}
            onClick={onZoomIn}
            aria-label={zoomAria?.in ?? "Zoom in"}
          >
            <Plus {...defaultIconProps} aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className={mapFrameClasses.toggle}
          onClick={onToggle}
          aria-pressed={isExpanded}
          aria-label={isExpanded ? toggleLabels.collapseAria : toggleLabels.expandAria}
          disabled={disableToggle}
        >
          {isExpanded ? (
            <>
              <ArrowsInSimple {...defaultIconProps} aria-hidden />
              {toggleLabels.collapseButton}
            </>
          ) : (
            <>
              <ArrowsOutSimple {...defaultIconProps} aria-hidden />
              {toggleLabels.expandButton}
            </>
          )}
        </button>
      </div>
      {mapContent ?? (
        <div
          ref={mapContainerRef ?? null}
          className={[
            mapFrameClasses.canvas,
            isExpanded ? mapFrameClasses.canvasExpanded : "",
            mapClassName ?? "",
          ]
            .filter(Boolean)
            .join(" ")}
          role="presentation"
        />
      )}
      {fabContent ? (
        <div
          className={mapFrameClasses.fabGroup}
          aria-label={fabAriaLabel ?? toggleLabels.expandAria}
        >
          {fabContent}
        </div>
      ) : null}
      {placeholder}
      {loadingLabel && status ? (
        <MapLoadingPill
          show={!status.hasRenderedTiles || !status.isMapReady}
          label={loadingLabel}
        />
      ) : null}
    </div>
  );
}
